import React, { useState, useRef } from 'react';
import { parseMidi } from 'midi-file';
import classNames from 'classnames';
import { FiFile, FiDownload } from 'react-icons/fi';

import { downloadMidi } from '../../util/downloadMidi';
import { CircleOfFifthsStyle } from './Styles';
import { LogoHeader } from '../components/LogoHeader';
import { getUnitCirclePoint } from '../../util/getUnitCirclePoint';
import { Title } from '../../components/Title';
import { HideHeader } from '../../routes/Dashboard';

const transposeMidi = (midiData, transposeAmount) => ({
   ...midiData,
   tracks: midiData.tracks.map((track) =>
      track.map((midiEvent) =>
         midiEvent.noteNumber
            ? {
                 ...midiEvent,
                 noteNumber: midiEvent.noteNumber + transposeAmount,
              }
            : midiEvent
      )
   ),
});

const transposeAndDownload = (file, transposeAmount) => {
   const name = file.name.replace(
      /\.midi?$/,
      `_t_${transposeAmount === 7 ? '5th' : '4th'}.mid`
   );
   const reader = new FileReader();
   reader.onload = (e) => {
      downloadMidi(
         transposeMidi(
            parseMidi(new Uint8Array(e.target.result)),
            transposeAmount
         ),
         name
      );
   };
   reader.readAsArrayBuffer(file);
};

const majorKeys = [
   { name: 'C' },
   { name: 'G' },
   { name: 'D' },
   { name: 'A' },
   { name: 'E' },
   { name: 'B' },
   { name: 'F♯' },
   { name: 'D♭' },
   { name: 'A♭' },
   { name: 'E♭' },
   { name: 'B♭' },
   { name: 'F' },
];

const minorKeys = [
   { name: 'a' },
   { name: 'e' },
   { name: 'b' },
   { name: 'f♯' },
   { name: 'c♯' },
   { name: 'g♯' },
   { name: 'd♯' },
   { name: 'b♭' },
   { name: 'f' },
   { name: 'c' },
   { name: 'g' },
   { name: 'd' },
];

const allKeys = [
   ...majorKeys.map((key, index) => ({ ...key, type: 'major', index })),
   ...minorKeys.map((key, index) => ({ ...key, type: 'minor', index })),
].sort(({ name: nameA }, { name: nameB }) => (nameA > nameB ? 1 : -1));

const outerCircleR = 200;
const outerCircleD = outerCircleR * 2;
const middleCircleR = 125;
// const middleCircleD = middleCircleR * 2;
const innerCircleR = 50;
const svgPadding = 5;
const gTranslate = `translate(${outerCircleR + svgPadding},${
   outerCircleR + svgPadding
})`;
const segments = majorKeys.length;
const segmentAngle = 360 / segments;
const initialAngleOffset = (-1 * segmentAngle) / 2;

const CircleSegment = ({ radius, segmentNumber }) => {
   const point1 = getUnitCirclePoint(
      initialAngleOffset + (segmentNumber - 1) * segmentAngle
   );
   const point2 = getUnitCirclePoint(
      initialAngleOffset + segmentNumber * segmentAngle
   );
   const x1 = point1[0] * radius;
   const y1 = -1 * point1[1] * radius;
   const x2 = point2[0] * radius;
   const y2 = -1 * point2[1] * radius;
   return (
      <path d={`M0 0 ${x1} ${y1} A ${radius} ${radius} 0 0 1 ${x2} ${y2} Z`} />
   );
};

const KeyText = ({ name, radius, segmentNumber }) => {
   const point = getUnitCirclePoint((segmentNumber - 1) * segmentAngle);

   const x = point[0] * radius;
   const y = -1 * point[1] * radius;

   let keyName = name;

   const isFlat = keyName.match(/♭/);
   const isSharp = keyName.match(/♯/);
   if (isFlat || isSharp) {
      keyName = keyName.replace(/[♭♯]/, '');
   }

   return (
      <text textAnchor="middle" dominantBaseline="middle" x={x} y={y}>
         {keyName}
         {isFlat && <tspan dx={-2}>♭</tspan>}
         {isSharp && <tspan>♯</tspan>}
      </text>
   );
};

const KeyName = ({ name }) => {
   let keyName = name;

   const isFlat = keyName.match(/♭/);
   const isSharp = keyName.match(/♯/);
   if (isFlat || isSharp) {
      keyName = keyName.replace(/[♭♯]/, '');
   }

   return (
      <span className="cof-key-name">
         {keyName}
         {isFlat && <sup className="cof-flat">♭</sup>}
         {isSharp && <sup>♯</sup>}
         {/* {!isFlat && !isSharp && <sup> </sup>} */}
      </span>
   );
};

const CircleSvg = ({
   selected,
   fourth,
   fifth,
   relative,
   onKeyClick,
   highlighted,
}) => (
   <svg
      className={classNames({
         'cof-circle-of-fifts': true,
         [`cof-${highlighted}-highlighted`]: highlighted,
      })}
      viewBox={`0 0 ${outerCircleD + svgPadding * 2} ${
         outerCircleD + svgPadding * 2
      }`}
   >
      <g transform={gTranslate}>
         {majorKeys.map((key, i) => (
            <g
               className={classNames({
                  'cof-major': true,
                  'cof-selected':
                     selected.index === i && selected.type === 'major',
                  'cof-fourth': fourth.index === i && fourth.type === 'major',
                  'cof-fifth': fifth.index === i && fifth.type === 'major',
                  'cof-relative':
                     relative.index === i && relative.type === 'major',
               })}
               key={key.name}
               onClick={() => onKeyClick(i, 'major')}
            >
               <CircleSegment radius={outerCircleR} segmentNumber={i + 1} />
               <KeyText
                  name={key.name}
                  radius={middleCircleR + (outerCircleR - middleCircleR) / 2}
                  segmentNumber={i + 1}
               />
            </g>
         ))}
      </g>
      <g transform={gTranslate}>
         {minorKeys.map((key, i) => (
            <g
               className={classNames({
                  'cof-minor': true,
                  'cof-selected':
                     selected.index === i && selected.type === 'minor',
                  'cof-fourth': fourth.index === i && fourth.type === 'minor',
                  'cof-fifth': fifth.index === i && fifth.type === 'minor',
                  'cof-relative':
                     relative.index === i && relative.type === 'minor',
               })}
               key={key.name}
               onClick={() => onKeyClick(i, 'minor')}
            >
               <CircleSegment radius={middleCircleR} segmentNumber={i + 1} />
               <KeyText
                  name={key.name}
                  radius={innerCircleR + (middleCircleR - innerCircleR) / 2}
                  segmentNumber={i + 1}
               />
            </g>
         ))}
      </g>
      <g transform={gTranslate}>
         <circle x="0" y="0" r={innerCircleR} className="cof-inner-circle" />
      </g>
   </svg>
);

const initialState = {
   selected: {
      index: null,
      type: null,
   },
   fourth: {
      index: null,
      type: null,
   },
   fifth: {
      index: null,
      type: null,
   },
   relative: {
      index: null,
      type: null,
   },
};

const HeaderAndTitle = ({ className }) => (
   <div className={className}>
      <LogoHeader />
      <h1>Circle of Fifths</h1>
   </div>
);

const Circle = () => {
   const [state, setState] = useState(initialState);
   const [hovered, setHovered] = useState(null);

   const selectKey = (keyIndex, keyType) => {
      setState((prevState) => {
         if (
            keyIndex == null ||
            (prevState.selected.index === keyIndex &&
               prevState.selected.type === keyType)
         ) {
            return initialState;
         }
         return {
            selected: { index: keyIndex, type: keyType },
            fifth: { index: (keyIndex + 1) % 12, type: keyType },
            fourth: { index: (keyIndex + 11) % 12, type: keyType },
            relative: {
               index: keyIndex,
               type: keyType === 'minor' ? 'major' : 'minor',
            },
         };
      });
   };

   return (
      <div className="cof-content-wrapper">
         <CircleSvg
            selected={state.selected}
            fourth={state.fourth}
            fifth={state.fifth}
            relative={state.relative}
            onKeyClick={selectKey}
            highlighted={hovered}
         />
         <div className="cof-content">
            <HeaderAndTitle className="cof-side-header" />
            <div className="cof-key-definitions">
               <table
                  className={classNames({
                     'cof-key-is-selected': state.selected.index !== null,
                  })}
               >
                  <tbody>
                     <tr
                        className="cof-original-key"
                        onMouseEnter={() => setHovered('selected')}
                        onMouseLeave={() => setHovered(null)}
                     >
                        <td>
                           <label htmlFor="cof-key-select">Original Key:</label>
                        </td>
                        <td>
                           <select
                              id="cof-key-select"
                              className="cof-key-select"
                              aria-label="Original Key"
                              value={
                                 state.selected.index !== null
                                    ? `${state.selected.index}_${state.selected.type}`
                                    : 'none'
                              }
                              onChange={(e) => {
                                 if (e.target.value === 'none') {
                                    setState(initialState);
                                 } else {
                                    const [index, type] =
                                       e.target.value.split('_');
                                    selectKey(Number(index), type);
                                 }
                              }}
                           >
                              <option value="none">--</option>
                              {allKeys.map(({ index, type, name }) => (
                                 <option
                                    value={`${index}_${type}`}
                                    key={`${index}_${type}`}
                                 >
                                    {name}
                                 </option>
                              ))}
                           </select>
                        </td>
                     </tr>
                     <tr
                        onMouseEnter={() => setHovered('fifth')}
                        onMouseLeave={() => setHovered(null)}
                     >
                        <td>Fifth:</td>
                        <td>
                           {state.fifth.index !== null && (
                              <KeyName
                                 name={
                                    (state.fifth.type === 'major'
                                       ? majorKeys
                                       : minorKeys)[state.fifth.index].name
                                 }
                              />
                           )}
                        </td>
                     </tr>
                     <tr
                        onMouseEnter={() => setHovered('fourth')}
                        onMouseLeave={() => setHovered(null)}
                     >
                        <td>Fourth:</td>
                        <td>
                           {state.fourth.index !== null && (
                              <KeyName
                                 name={
                                    (state.fourth.type === 'major'
                                       ? majorKeys
                                       : minorKeys)[state.fourth.index].name
                                 }
                              />
                           )}
                        </td>
                     </tr>
                     <tr
                        onMouseEnter={() => setHovered('relative')}
                        onMouseLeave={() => setHovered(null)}
                     >
                        <td>
                           Relative{' '}
                           {state.relative.type === 'major' ? 'Major' : 'Minor'}
                           :
                        </td>
                        <td>
                           {state.relative.index !== null && (
                              <KeyName
                                 name={
                                    (state.relative.type === 'major'
                                       ? majorKeys
                                       : minorKeys)[state.relative.index].name
                                 }
                              />
                           )}
                        </td>
                     </tr>
                  </tbody>
               </table>
            </div>
            {/* <div className="cof-lead-magnet">
               <h2>Want to learn Music Theory?</h2>
               <p>
                  If you want to learn more about music theory, check out my
                  exercise,
                  <a
                     href="https://offers.earinstructor.com/exercises/chord-captain/"
                     target="_blank"
                  >
                     <em>Chord Captain</em>
                  </a>
                  . I think you&apos;ll find it very helpful.
               </p>
            </div> */}
            <Transpose />
         </div>
      </div>
   );
};

const Transpose = () => {
   const [file, setFile] = useState(null);
   const [transposeAmount, setTransposeAmount] = useState('7');
   const [fileDragStatus, setFileDragStatus] = useState('pending');

   const fileInputRef = useRef();

   const handleDrop = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setFileDragStatus('pending');
      fileInputRef.current.files = e.dataTransfer.files;
      setFile(e.dataTransfer.files[0]);
   };
   const handleDragOver = (e) => {
      e.stopPropagation();
      e.preventDefault();
      setFileDragStatus('dragging');
   };
   const handleDragLeave = (e) => {
      e.stopPropagation();
      e.preventDefault();
      setFileDragStatus('pending');
   };

   return (
      <div className="cof-transpose">
         <h2>Transpose Midi</h2>
         <form
            className="selectable"
            onSubmit={(e) => {
               e.preventDefault();
               if (!(file?.type === 'audio/midi')) {
                  alert(
                     'You must select a MIDI file to transpose. Cannot transpose other files.'
                  );
                  return;
               }
               transposeAndDownload(file, Number(transposeAmount));
            }}
         >
            <div className="cof-drag-drop-wrapper">
               <label
                  htmlFor="cof-file-input"
                  className={classNames({
                     'cof-drag-drop': true,
                     'cof-is-dragging': fileDragStatus === 'dragging',
                     'cof-invalid-file': file && file.type !== 'audio/midi',
                  })}
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onDragEnter={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDragEnd={handleDragLeave}
               >
                  <FiFile size="25" />
                  {file?.type === 'audio/midi' ? (
                     <strong>{file.name}</strong>
                  ) : (
                     <span>
                        <strong>Drag and Drop</strong> or <strong>Click</strong>{' '}
                        to select a MIDI file.
                     </span>
                  )}
               </label>
               <input
                  id="cof-file-input"
                  className="cof-drag-drop-input"
                  type="file"
                  onChange={(e) => setFile(e.target.files[0])}
                  ref={fileInputRef}
                  accept=".mid,.midi"
               />
            </div>
            <div className="cof-fields">
               <div>
                  <label htmlFor="cof-file-transpose">Transpose To:</label>
                  <select
                     id="cof-file-transpose"
                     onChange={(e) => setTransposeAmount(e.target.value)}
                     value={transposeAmount}
                  >
                     <option value="7">5th</option>
                     <option value="5">4th</option>
                  </select>
               </div>
               <div>
                  <button type="submit">
                     <FiDownload /> Download
                  </button>
               </div>
            </div>
         </form>
      </div>
   );
};

const CircleOfFifths = () => (
   <CircleOfFifthsStyle>
      <Title
         title="Interactive Circle of Fifths"
         description="Learn music theory with this interactive circle of fifths: perfect fifth, perfect fourth, key signatures, relative minors, transpose midi, and more!"
      />
      <HideHeader />
      <HeaderAndTitle className="cof-top-header" />
      <Circle />
   </CircleOfFifthsStyle>
);

// needed for code splitting
// eslint-disable-next-line import/no-default-export
export default CircleOfFifths;
